import { useEffect, useRef, useState } from 'react';
import type { IChartApi } from 'lightweight-charts';
import { useMarketStore } from '../../store/marketStore';
import { useDrawingTools } from '../../store/drawingStore';
import { useActivePaneDrawings } from '../../store/drawingPanes';
import { useChartStore } from '../../store/chartStore';
import { useAuthStore } from '../../store/authStore';
import { captureChartSnapshot, canvasToBlob, downloadCanvas } from '../../utils/chartSnapshot';
import { ChartSettingsModal } from './ChartSettingsModal';
import { PositionCalculator } from './PositionCalculator';
import { AlertsManager } from './AlertsManager';
import { AccountMenu } from '../Auth/AccountMenu';

interface ToolbarProps {
  chartRef:     React.RefObject<IChartApi | null>;
  chartAreaRef: React.RefObject<HTMLDivElement>;
}

function UndoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function FullscreenEnterIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function FullscreenExitIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M9 3v3a2 2 0 0 1-2 2H4M15 3v3a2 2 0 0 0 2 2h3M9 21v-3a2 2 0 0 0-2-2H4M15 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  );
}

function CalculatorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="11" x2="8" y2="11.01" />
      <line x1="12" y1="11" x2="12" y2="11.01" />
      <line x1="16" y1="11" x2="16" y2="11.01" />
      <line x1="8" y1="15" x2="8" y2="15.01" />
      <line x1="12" y1="15" x2="12" y2="15.01" />
      <line x1="16" y1="15" x2="16" y2="15.01" />
      <line x1="8" y1="19" x2="8" y2="19.01" />
      <line x1="12" y1="19" x2="12" y2="19.01" />
      <line x1="16" y1="19" x2="16" y2="19.01" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

const SNAPSHOT_ACTIONS = [
  { key: 'download', label: 'Download image' },
  { key: 'copy-image', label: 'Copy image' },
  { key: 'copy-link', label: 'Copy link' },
  { key: 'new-tab', label: 'Open in new tab' },
] as const;

function SnapshotMenu({ chartRef, chartAreaRef }: ToolbarProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeSymbol = useMarketStore((s) => s.activeSymbol);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const run = async (key: (typeof SNAPSHOT_ACTIONS)[number]['key']) => {
    setOpen(false);

    if (key === 'copy-link') {
      try { await navigator.clipboard.writeText(window.location.href); } catch { /* clipboard unavailable */ }
      return;
    }

    const chart = chartRef.current;
    const area  = chartAreaRef.current;
    if (!chart || !area) return;
    const canvas = captureChartSnapshot(chart, area);

    if (key === 'download') {
      downloadCanvas(canvas, `${activeSymbol}_${Date.now()}.png`);
    } else if (key === 'copy-image') {
      try {
        const blob = await canvasToBlob(canvas);
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      } catch { /* clipboard image write unsupported/blocked */ }
    } else if (key === 'new-tab') {
      try {
        const blob = await canvasToBlob(canvas);
        window.open(URL.createObjectURL(blob), '_blank');
      } catch { /* ignore */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Chart snapshot"
        className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
      >
        <CameraIcon />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 py-1"
          style={{ background: 'var(--bg-panel-alt)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.45)', width: 168, zIndex: 1100 }}
        >
          <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wide text-[var(--text-muted)]">Chart snapshot</div>
          {SNAPSHOT_ACTIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => run(key)}
              className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--accent)] hover:text-white"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FullscreenButton() {
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  return (
    <button
      onClick={toggle}
      title={isFullscreen ? 'Exit full screen' : 'Full screen'}
      className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
    >
      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
    </button>
  );
}

function AuthControls() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const openAuthModal = useAuthStore((s) => s.openAuthModal);

  // Same footprint as the "Log in" button so resolving /auth/me doesn't shift the toolbar.
  if (status === 'checking') return <div className="w-[68px] h-[30px]" aria-hidden />;

  if (status === 'anonymous' || !user) {
    return (
      <button
        onClick={() => openAuthModal('login')}
        className="w-[68px] h-[30px] inline-flex items-center justify-center gap-1.5 rounded-md text-xs font-medium text-white bg-[var(--accent)] hover:bg-[var(--accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--bg-panel)]"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <path d="m10 17 5-5-5-5M15 12H3" />
        </svg>
        Log in
      </button>
    );
  }

  return <AccountMenu user={user} />;
}

export function Toolbar({ chartRef, chartAreaRef }: ToolbarProps) {
  const settingsOpen = useChartStore((s) => s.settingsOpen);
  const setSettingsOpen = useChartStore((s) => s.setSettingsOpen);
  const [calcOpen, setCalcOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  // Undo/redo act on the active pane's history.
  const drawingsLocked = useDrawingTools((s) => s.drawingsLocked);
  const canUndo = useActivePaneDrawings((s) => s.history.length > 0) && !drawingsLocked;
  const canRedo = useActivePaneDrawings((s) => s.redoStack.length > 0) && !drawingsLocked;
  const undo = useActivePaneDrawings((s) => s.undo);
  const redo = useActivePaneDrawings((s) => s.redo);

  return (
    <header className="flex items-center justify-between px-4 py-1 bg-[var(--bg-panel)] border-b border-[var(--border-color)] select-none">
      <div className="flex items-center">
        <span className="text-blue-400 font-bold text-sm tracking-wide">Check Your Chart</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
        <button
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-[var(--text-muted)]"
        >
          <UndoIcon />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
          className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-[var(--text-muted)]"
        >
          <RedoIcon />
        </button>
        <SnapshotMenu chartRef={chartRef} chartAreaRef={chartAreaRef} />
        <button
          onClick={() => setCalcOpen(true)}
          title="Position Calculator"
          className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
        >
          <CalculatorIcon />
        </button>
        <button
          onClick={() => setAlertsOpen(true)}
          title="Alerts"
          className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
        >
          <BellIcon />
        </button>
        <FullscreenButton />
        <button
          onClick={() => setSettingsOpen(true)}
          title="Settings"
          className="w-9 h-9 flex items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
        >
          <GearIcon />
        </button>
        <span>Real-Time Analytics Platform</span>
        <div className="ml-2 pl-3 flex items-center border-l border-[var(--border-color)]">
          <AuthControls />
        </div>
      </div>
      {settingsOpen && <ChartSettingsModal onClose={() => setSettingsOpen(false)} />}
      {calcOpen && <PositionCalculator onClose={() => setCalcOpen(false)} />}
      {alertsOpen && <AlertsManager onClose={() => setAlertsOpen(false)} />}
    </header>
  );
}
